<template>
  <div class="sv-filter" :data-test="`sv-filter-${name}`">
    <button
      type="button"
      class="sv-filter__button"
      :class="{ 'sv-filter__button--active': selected.length > 0 }"
      :disabled="!options.length"
      :aria-expanded="open ? 'true' : 'false'"
      :data-test="`sv-filter-${name}-button`"
      @click="toggle"
    >
      <span class="sv-filter__label">{{ label }}:</span>
      <span class="sv-filter__value">{{ summary }}</span>
      <span class="sv-filter__caret" aria-hidden="true">&#9662;</span>
    </button>

    <!--
      On a phone the panel is a bottom sheet (see the phone @media block), and a sheet needs a
      backdrop: it dims the schedule so the sheet reads as on top of it, and tapping it closes the
      sheet — the gesture people already use to dismiss one. Hidden by CSS on wider screens.
    -->
    <div
      v-if="open"
      class="sv-filter__backdrop"
      aria-hidden="true"
      :data-test="`sv-filter-${name}-backdrop`"
      @click="open = false"
    ></div>

    <div v-if="open" class="sv-filter__panel" :data-test="`sv-filter-${name}-panel`">
      <div class="sv-filter__panel-head">
        <span>{{ label }}</span>
        <button
          type="button"
          class="sv-filter__clear"
          :disabled="!selected.length"
          :data-test="`sv-filter-${name}-clear`"
          @click="clear"
        >Show all</button>
      </div>

      <ul class="sv-filter__list">
        <li v-for="opt in options" :key="opt.value">
          <label class="sv-filter__option">
            <input
              type="checkbox"
              :checked="isSelected(opt.value)"
              :data-test="`sv-filter-${name}-option-${opt.value}`"
              @change="toggleOption(opt.value)"
            />
            <span class="sv-filter__option-text">{{ opt.label }}</span>
          </label>
        </li>
      </ul>

      <!-- Phone only. Choices apply as they are ticked; this just puts the sheet away. -->
      <div class="sv-filter__done-row">
        <button
          type="button"
          class="sv-filter__done"
          :data-test="`sv-filter-${name}-done`"
          @click="open = false"
        >Done</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * One narrowing control — Provider, Operatory or Status.
 *
 * Empty selection means "show everything", never "show nothing" (see utils/scheduleView/filters.js
 * for why). The button says which, so a narrowed schedule can never be mistaken for a quiet day
 * from the toolbar alone.
 *
 * Emits `change` with the new array and owns no state but whether it is open — the filters live in
 * the store so they survive a date change and are remembered per browser.
 */
export default {
  name: 'ScheduleFilterDropdown',

  props: {
    /** Used for the data-test hooks: 'provider' | 'operatory' | 'status'. */
    name: { type: String, required: true },
    label: { type: String, required: true },
    /** [{ value, label }] — already narrowed to what is actually on the day. */
    options: { type: Array, default: () => [] },
    /** The selected values. Empty = no filter. */
    selected: { type: Array, default: () => [] },
  },

  data() {
    return { open: false };
  },

  computed: {
    summary() {
      if (!this.options.length) return 'None today';
      if (!this.selected.length) return 'All';
      if (this.selected.length === 1) {
        const hit = this.options.find(o => String(o.value) === String(this.selected[0]));
        return hit ? hit.label : '1 selected';
      }
      return `${this.selected.length} selected`;
    },
  },

  // A dropdown that only closes on its own button traps people behind it on a tablet, where there
  // is no Escape key and the next thing they tap is the schedule itself.
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    toggle() {
      this.open = !this.open;
    },

    isSelected(value) {
      return this.selected.some(v => String(v) === String(value));
    },

    toggleOption(value) {
      const next = this.isSelected(value)
        ? this.selected.filter(v => String(v) !== String(value))
        : this.selected.concat([value]);
      this.$emit('change', next);
    },

    clear() {
      this.$emit('change', []);
    },

    onDocumentClick(event) {
      if (!this.open) return;
      if (this.$el && this.$el.contains(event.target)) return;
      this.open = false;
    },

    onKeydown(event) {
      if (event.key === 'Escape' && this.open) this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-filter {
  position: relative;
}

.sv-filter__button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 34px;
  max-width: 220px;
  padding: 0 10px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  color: $sv-ink;
  font-size: 13px;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: $sv-brand; }
  &:disabled { opacity: 0.5; cursor: default; }
  &--active { border-color: $sv-brand; color: $sv-brand; font-weight: 600; }
}

.sv-filter__label {
  color: $sv-ink-3;
  flex: 0 0 auto;
}

.sv-filter__button--active .sv-filter__label { color: inherit; }

.sv-filter__value {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sv-filter__caret {
  font-size: 10px;
  flex: 0 0 auto;
}

.sv-filter__panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  min-width: 220px;
  max-width: 300px;
  max-height: 320px;
  overflow-y: auto;
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  box-shadow: 0 8px 24px rgba(21, 32, 26, 0.14);
}

.sv-filter__panel-head {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: $sv-surface;
  border-bottom: 1px solid $sv-line;
  font-size: 12px;
  font-weight: 700;
  color: $sv-ink-3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sv-filter__clear {
  border: 0;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: $sv-brand;
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: default; }
}

.sv-filter__list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.sv-filter__option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  // A 36px row is a comfortable tap target on the operatory iPad; 24px is not.
  min-height: 36px;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 500;
  color: $sv-ink;
  cursor: pointer;

  &:hover { background: $sv-bg; }
}

.sv-filter__option-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sv-filter__backdrop,
.sv-filter__done-row {
  display: none;
}

/**
 * Phone: a bottom sheet, not a dropdown.
 *
 * A 220px panel hung off a button near the right of a 390px screen runs off the glass, and the
 * rows are too small for a thumb. A sheet is full width, sits where the thumb already is, and has
 * room for 48px rows.
 *
 * z-index 10000+: above the AI agent's floating button (agent-panel.css, 9999 !important), which
 * otherwise sits on top of the sheet's lower rows on a phone. A sheet is modal; it covers it.
 */
@media (max-width: $sv-phone-max) {
  .sv-filter__backdrop {
    display: block;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10000;
    background: rgba(21, 32, 26, 0.32);
  }

  .sv-filter__panel {
    position: fixed;
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10001;
    min-width: 0;
    max-width: none;
    max-height: 70vh;
    border-bottom: 0;
    border-radius: 16px 16px 0 0;
    padding-bottom: env(safe-area-inset-bottom);
    box-shadow: 0 -8px 30px rgba(21, 32, 26, 0.18);
    // A scroll that hits the end of the list must not carry on scrolling the schedule behind it.
    overscroll-behavior: contain;
  }

  .sv-filter__panel-head {
    padding: 14px 16px;
    font-size: 13px;
  }

  .sv-filter__clear { font-size: 14px; }

  .sv-filter__option {
    min-height: 48px;
    padding: 0 16px;
    font-size: 15px;

    input { width: 20px; height: 20px; }
  }

  .sv-filter__done-row {
    display: block;
    position: sticky;
    bottom: 0;
    padding: 10px 16px;
    background: $sv-surface;
    border-top: 1px solid $sv-line;
  }

  .sv-filter__done {
    width: 100%;
    height: 44px;
    border: 0;
    border-radius: $sv-radius;
    background: $sv-brand;
    color: #ffffff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
  }
}
</style>
